import {
  checkIsMastraErrorToolOutput,
  type MastraErrorToolOutput,
} from "@repo/app-types/agents/mastraErrorToolOutput";
import type { SimulateAgentConversationToolOutput } from "@repo/app-types/entities/simulationAgent";
import type { SimulateVoiceAgentConversationToolInput } from "@repo/app-types/entities/teammateAgentProposal";
import { safeParseJSON } from "@repo/app-types/helpers/utilities";
import {
  ChatToolBase,
  ChatToolDisclosure,
  type ToolComponent,
} from "@repo/ui/chat/chatToolCall";
import {
  IconChevronDown,
  IconChevronUp,
  IconMessages,
} from "@tabler/icons-react";
import type { FC } from "react";
import { Button, Disclosure, Heading } from "react-aria-components";
import { twMerge } from "tailwind-merge";

export const SimulateVoiceAgentConversationTool: ToolComponent<
  SimulateVoiceAgentConversationToolInput,
  SimulateAgentConversationToolOutput | MastraErrorToolOutput
> = (props) => {
  const scenario = props.input.scenario;
  const isRunning = props.status.type === "running";
  const isComplete = props.status.type === "complete";

  // If running, show what's being simulated
  if (isRunning) {
    const testingText = scenario
      ? `Simulating: "${truncate(scenario, 50)}"`
      : "Running simulation...";

    return <ChatToolBase status={props.status}>{testingText}</ChatToolBase>;
  }

  if (checkIsMastraErrorToolOutput(props.output)) {
    return (
      <ChatToolBase status={props.status}>
        An error occurred while running the simulation
      </ChatToolBase>
    );
  }

  // If complete, show expandable results
  // Validate that result has the expected shape before rendering
  if (
    isComplete &&
    props.output &&
    Array.isArray(props.output.transcript) &&
    typeof props.output.commentary === "string"
  ) {
    return (
      <ChatToolDisclosure
        status={props.status}
        icon={
          <IconMessages className="mt-0.5 size-3.5 shrink-0 text-indigo-500" />
        }
        label={<>Simulation: {scenario ? `"${scenario}"` : "completed"}</>}
      >
        <SimulationDetails scenario={scenario} result={props.output} />
      </ChatToolDisclosure>
    );
  }

  // Fallback
  return (
    <ChatToolBase status={props.status}>Running simulation...</ChatToolBase>
  );
};

interface SimulationDetailsProps {
  scenario: string;
  result: SimulateAgentConversationToolOutput;
}

const SimulationDetails: FC<SimulationDetailsProps> = ({
  scenario,
  result,
}) => {
  const { transcript, commentary } = result;

  return (
    <div className="space-y-2">
      {/* Show the scenario */}
      <div className="space-y-1">
        <div className="text-[10px] font-medium text-gray-400">Scenario</div>
        <div className="text-xs text-gray-600">{scenario}</div>
      </div>

      {/* Show the transcript */}
      {transcript.length > 0 && (
        <div className="space-y-1">
          <div className="text-[10px] font-medium text-gray-400">
            Transcript
          </div>
          {transcript.map((msg, i) => (
            <MessageBubble key={i} msg={msg} />
          ))}
        </div>
      )}

      {/* Show the commentary/analysis */}
      {commentary && (
        <div className="space-y-1">
          <div className="text-[10px] font-medium text-gray-400">Analysis</div>
          <div className="rounded-lg bg-amber-50 px-2.5 py-1.5 text-xs whitespace-pre-wrap text-amber-900">
            {commentary}
          </div>
        </div>
      )}
    </div>
  );
};

interface MessageBubbleProps {
  msg: {
    role: "user" | "assistant" | "tool";
    content: string;
    toolInput: string | null;
  };
}

const MessageBubble: FC<MessageBubbleProps> = ({ msg }) => {
  const renderMessage = () => {
    if (msg.role !== "tool") {
      return <span className="whitespace-pre-wrap">{msg.content}</span>;
    }

    return (
      <div className="space-y-2">
        <div className="font-medium not-italic">Tool:</div>
        {msg.toolInput ? (
          <div>
            <div className="mb-1 text-[10px] font-medium text-muted-foreground">
              Input
            </div>
            <ExpandableText text={msg.toolInput} />
          </div>
        ) : null}
        {msg.content ? (
          <div>
            <div className="mb-1 text-[10px] font-medium text-muted-foreground">
              Output
            </div>
            <ExpandableText text={msg.content} />
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div
      className={twMerge(
        "rounded-lg px-2.5 py-1.5 text-xs",
        msg.role === "user" && "bg-indigo-50 text-indigo-900",
        msg.role === "assistant" && "bg-gray-100 text-foreground",
        msg.role === "tool" && "bg-amber-50 text-amber-800",
      )}
    >
      {renderMessage()}
    </div>
  );
};

function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + "...";
}

interface ExpandableTextProps {
  text: string;
  maxPreviewLength?: number;
  className?: string;
}

/**
 * Component that displays text with expand/collapse functionality.
 * Automatically formats JSON strings if detected.
 */
const ExpandableText: FC<ExpandableTextProps> = ({
  text,
  maxPreviewLength = 150,
  className,
}) => {
  // Try to parse as JSON and format it
  const parsedJson = safeParseJSON(text);
  const isJson = parsedJson !== null;
  const formattedText = isJson ? JSON.stringify(parsedJson, null, 2) : text;

  const shouldTruncate = formattedText.length > maxPreviewLength;

  const preClassName = twMerge(
    "font-sans whitespace-pre-wrap",
    isJson && "rounded-sm bg-gray-100 p-2",
    className,
  );

  if (!shouldTruncate) {
    return <pre className={preClassName}>{formattedText}</pre>;
  }

  const truncatedText = truncate(formattedText, maxPreviewLength);

  return (
    <Disclosure>
      {({ isExpanded }) => (
        <div className="space-y-1">
          <pre className={preClassName}>
            {isExpanded ? formattedText : truncatedText}
          </pre>
          <Heading>
            <Button
              slot="trigger"
              className="flex items-center gap-1 text-xs text-gray-600 hover:text-foreground"
            >
              {isExpanded ? (
                <>
                  <IconChevronUp className="h-3 w-3" />
                  Show less
                </>
              ) : (
                <>
                  <IconChevronDown className="h-3 w-3" />
                  Show more
                </>
              )}
            </Button>
          </Heading>
        </div>
      )}
    </Disclosure>
  );
};
